// The API treats action timestamps as wall-clock values in the logged-in site's
// time zone and converts them to UTC. Keep the returned value offset-free so it
// is not mistaken for an already-converted UTC timestamp.
const windowsToIanaTimeZone = {
  'Eastern Standard Time': 'America/New_York',
  'Central Standard Time': 'America/Chicago',
  'Mountain Standard Time': 'America/Denver',
  'US Mountain Standard Time': 'America/Phoenix',
  'Pacific Standard Time': 'America/Los_Angeles',
  'Alaskan Standard Time': 'America/Anchorage',
  'Hawaiian Standard Time': 'Pacific/Honolulu',
  'Atlantic Standard Time': 'America/Halifax',
  UTC: 'UTC'
};

const getCurrentDateTimeForTimeZone = (timeZone, date = new Date()) => {
  if (!timeZone) {
    throw new Error('A site time zone is required to capture the action start time.');
  }

  const resolvedTimeZone = windowsToIanaTimeZone[timeZone] || timeZone;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: resolvedTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });
  const parts = formatter.formatToParts(date).reduce((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value;
    return result;
  }, {});

  const milliseconds = String(date.getUTCMilliseconds()).padStart(3, '0');
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}.${milliseconds}`;
};

// API timestamps are stored/returned in UTC but often serialized without a Z
// or offset suffix (e.g. "2026-09-18T18:53:17"). JS treats such "naive"
// strings as browser-local time when parsed directly, which silently swaps
// in the viewer's own time zone instead of UTC. Force UTC interpretation for
// any string lacking an explicit zone marker before converting for display.
const parseApiTimestamp = (dateValue) => {
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateValue) && !/(Z|[+-]\d{2}:?\d{2})$/.test(dateValue)) {
    return new Date(dateValue + 'Z');
  }
  return new Date(dateValue);
};

// Returns the calendar day (YYYY-MM-DD) that a timestamp falls on in the given
// site time zone, so date-range comparisons match the site's local day instead
// of the browser's local day (which can differ per state and cause off-by-one
// day results, e.g. an action completed late in the site's day showing up as
// the next day when compared using the viewer's own time zone).
const getDateKeyForTimeZone = (timeZone, dateValue) => {
  if (!dateValue) return null;

  const date = parseApiTimestamp(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  const resolvedTimeZone = (timeZone && windowsToIanaTimeZone[timeZone]) || timeZone;
  if (!resolvedTimeZone) {
    // Fall back to the raw UTC calendar day when no site time zone is known.
    return date.toISOString().split('T')[0];
  }

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: resolvedTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(date);
};

// Formats a timestamp for display as MM/DD/YYYY h:mm:ss AM/PM in the given
// site time zone, instead of the browser's local time zone.
const formatDateTimeForTimeZone = (timeZone, dateValue) => {
  if (!dateValue) return '';

  const date = parseApiTimestamp(dateValue);
  if (Number.isNaN(date.getTime())) return '';

  const resolvedTimeZone = (timeZone && windowsToIanaTimeZone[timeZone]) || timeZone;
  if (!resolvedTimeZone) return '';

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: resolvedTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
  const parts = formatter.formatToParts(date).reduce((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value;
    return result;
  }, {});

  return `${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute}:${parts.second} ${parts.dayPeriod}`;
};

const SiteTimeUtility = { getCurrentDateTimeForTimeZone, getDateKeyForTimeZone, formatDateTimeForTimeZone };

export default SiteTimeUtility;
